import { type Change, isBlockRangeSelection } from '@gitbook/slate';
import { assertNever } from '@gitbook/util-ts';

import { createParagraph } from '../paragraph/utils';
import { LEAF_TEXT_BLOCKS } from '../types';

/**
 * Focus the start or end of the document, inserting an empty paragraph if needed.
 */
export function moveToEdgeLeafBlock(
    change: Change,
    position: 'DOCUMENT_START' | 'DOCUMENT_END'
): Change {
    const { document, selection } = change.value;

    if (isBlockRangeSelection(selection)) {
        change.switchSelection();
    }

    const block = (() => {
        switch (position) {
            case 'DOCUMENT_START':
                return document.nodes.first();
            case 'DOCUMENT_END':
                return document.nodes.last();
            default:
                assertNever(position);
        }
    })();

    if (!block) {
        return change;
    }

    // if the block is a paragraph, focus it
    if (LEAF_TEXT_BLOCKS.includes(block.type)) {
        switch (position) {
            case 'DOCUMENT_START':
                return change.collapseToStartOf(block).focus();
            case 'DOCUMENT_END':
                return change.collapseToEndOf(block).focus();
            default:
                assertNever(position);
        }
    }

    // otherwise append a paragraph and focus it
    const paragraph = createParagraph();

    return change
        .insertNodeByKey(
            document.key,
            { DOCUMENT_START: 0, DOCUMENT_END: document.nodes.size }[position],
            paragraph
        )
        .collapseToEndOf(paragraph)
        .focus();
}
